import React, { useState, useRef, useEffect } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Alert } from '../components/ui/alert'
import { toast } from 'sonner'
import { Search, CheckCircle, XCircle, AlertTriangle, Undo, Package, Plus, List, Calendar } from 'lucide-react'
import { OrderWithScan, ScanResult, OrderItemWithScan } from '../types/barcode'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'

export default function BarcodeOrderControlPage() {
    const { accessToken } = useAuthStore()
    const [orderNumber, setOrderNumber] = useState('')
    const [order, setOrder] = useState<OrderWithScan | null>(null)
    const [loading, setLoading] = useState(false)
    const [scanInput, setScanInput] = useState('')
    const [scanning, setScanning] = useState(false)
    const [lastScanResult, setLastScanResult] = useState<ScanResult | null>(null)
    const [selectedImage, setSelectedImage] = useState<string | null>(null)
    const [showCompletedOrders, setShowCompletedOrders] = useState(false)
    const [completedOrders, setCompletedOrders] = useState<any[]>([])
    const [loadingCompleted, setLoadingCompleted] = useState(false)
    const [completedSearchQuery, setCompletedSearchQuery] = useState('')
    const [selectedCompletedOrder, setSelectedCompletedOrder] = useState<any>(null)
    const [showStatusModal, setShowStatusModal] = useState(false)
    const [statusFilter, setStatusFilter] = useState('all')

    const scanInputRef = useRef<HTMLInputElement>(null)

    // Barkod input'una otomatik focus
    useEffect(() => {
        if (order && scanInputRef.current) {
            scanInputRef.current.focus()
        }
    }, [order])

    // Sipariş ara
    const searchOrder = async () => {
        if (!orderNumber.trim()) {
            toast.error('Sipariş numarası gerekli')
            return
        }
        await searchOrderWithNumber(orderNumber.trim())
    }

    // Sipariş ara (numara ile)
    const searchOrderWithNumber = async (orderNum: string) => {
        if (!orderNum.trim()) {
            toast.error('Sipariş numarası gerekli')
            return
        }

        setLoading(true)
        try {
            console.log('🔑 Access Token:', accessToken ? 'Token mevcut' : 'Token yok')
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/by-number/${orderNum.trim()}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (!response.ok) {
                throw new Error('Sipariş bulunamadı')
            }

            const orderData = await response.json()
            setOrder(orderData)
            toast.success('Sipariş bulundu')
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Sipariş arama hatası')
            setOrder(null)
        } finally {
            setLoading(false)
        }
    }

    // Barkod tara
    const scanBarcode = async () => {
        if (!scanInput.trim() || !order) return

        setScanning(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/${order.id}/scan`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ barcode: scanInput.trim() })
            })

            const result: ScanResult = await response.json()
            setLastScanResult(result)

            // Ses efekti
            if (result.result === 'success') {
                // Başarı sesi (yüksek ton)
                playBeep(800, 200)
                toast.success(result.message)
            } else if (result.result === 'over') {
                // Uyarı sesi (düşük ton)
                playBeep(400, 500)
                toast.warning(result.message)
            } else {
                // Hata sesi (çok düşük ton)
                playBeep(200, 800)
                toast.error(result.message)
            }

            // Order'ı güncelle
            if (result.item) {
                setOrder(prev => {
                    if (!prev) return null;

                    const updatedOrderItems = prev.orderItems.map(item =>
                        item.id === result.item!.id ? result.item! : item
                    );

                    const totalQuantity = updatedOrderItems.reduce((sum, item) => sum + item.quantity, 0);
                    const scannedQuantity = updatedOrderItems.reduce((sum, item) => sum + item.scannedQty, 0);
                    const completedItems = updatedOrderItems.filter(item => item.state === 'ok').length;
                    const progress = totalQuantity > 0 ? (scannedQuantity / totalQuantity * 100) : 0;

                    console.log('Frontend barkod tarama sonrası güncelleme:', {
                        totalQuantity,
                        scannedQuantity,
                        completedItems,
                        progress
                    });

                    return {
                        ...prev,
                        orderItems: updatedOrderItems,
                        summary: {
                            ...prev.summary,
                            totalQuantity,
                            scannedQuantity,
                            completedItems,
                            progress
                        }
                    };
                })
            }

            setScanInput('')
        } catch (error) {
            toast.error('Tarama hatası')
        } finally {
            setScanning(false)
        }
    }

    // Ses çal
    const playBeep = (frequency: number, duration: number) => {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
            const oscillator = audioContext.createOscillator()
            const gainNode = audioContext.createGain()

            oscillator.connect(gainNode)
            gainNode.connect(audioContext.destination)

            oscillator.frequency.value = frequency
            oscillator.type = 'square'

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000)

            oscillator.start(audioContext.currentTime)
            oscillator.stop(audioContext.currentTime + duration / 1000)
        } catch (e) {
            // Ses çalma hatası - sessiz devam et
        }
    }

    // Son taramayı geri al
    const undoLastScan = async () => {
        if (!order) return

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/${order.id}/undo-scan`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (response.ok) {
                const result = await response.json()
                toast.success(result.message)

                // Order'ı yenile
                searchOrder()
            } else {
                toast.error('Geri alma işlemi başarısız')
            }
        } catch (error) {
            toast.error('Geri alma hatası')
        }
    }

    // Manuel ürün işaretle
    const manualTick = async (item: OrderItemWithScan) => {
        if (!order) return

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/${order.id}/manual-tick/${item.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ increment: 1 })
            })

            if (response.ok) {
                const result = await response.json()
                toast.success(result.message)

                // Order'ı güncelle
                setOrder(prev => {
                    if (!prev) return null;

                    const updatedOrderItems = prev.orderItems.map(orderItem =>
                        orderItem.id === result.item.id ? result.item : orderItem
                    );

                    const totalQuantity = updatedOrderItems.reduce((sum, item) => sum + item.quantity, 0);
                    const scannedQuantity = updatedOrderItems.reduce((sum, item) => sum + item.scannedQty, 0);
                    const completedItems = updatedOrderItems.filter(item => item.state === 'ok').length;
                    const progress = totalQuantity > 0 ? (scannedQuantity / totalQuantity * 100) : 0;

                    console.log('Frontend order güncelleme:', {
                        totalQuantity,
                        scannedQuantity,
                        completedItems,
                        progress,
                        updatedItems: updatedOrderItems.map(item => ({
                            id: item.id,
                            code: item.code,
                            quantity: item.quantity,
                            scannedQty: item.scannedQty,
                            state: item.state
                        }))
                    });

                    return {
                        ...prev,
                        orderItems: updatedOrderItems,
                        summary: {
                            ...prev.summary,
                            totalQuantity,
                            scannedQuantity,
                            completedItems,
                            progress
                        }
                    };
                })
            } else {
                toast.error('Manuel işaretleme başarısız')
            }
        } catch (error) {
            toast.error('Manuel işaretleme hatası')
        }
    }

    // Siparişi tamamla
    const completeOrder = async (allowPartial = false) => {
        if (!order) {
            console.log('Order yok, tamamlama iptal edildi')
            return
        }

        console.log('Sipariş tamamlama başlıyor:', { orderId: order.id, allowPartial })

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/${order.id}/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ allowPartial })
            })

            console.log('Response status:', response.status)
            const result = await response.json()
            console.log('Response result:', result)

            if (response.ok) {
                toast.success(result.message)
                setOrder(null)
                setOrderNumber('')
                setLastScanResult(null)
            } else if (result.requiresConfirmation) {
                // Onay modali göster
                if (confirm(`${result.error}\\n\\nYine de tamamlamak istiyor musunuz?`)) {
                    completeOrder(true)
                }
            } else {
                toast.error(result.error)
            }
        } catch (error) {
            console.error('Tamamlama hatası:', error)
            toast.error('Tamamlama hatası')
        }
    }

    // Siparişleri getir (tüm durumlar)
    const fetchCompletedOrders = async (searchQuery = '', status = 'all') => {
        setLoadingCompleted(true)
        try {
            const params = new URLSearchParams({
                page: '1',
                size: '20'
            })

            if (searchQuery.trim()) {
                params.append('query', searchQuery.trim())
            }

            if (status !== 'all') {
                params.append('status', status)
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/completed?${params}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (response.ok) {
                const data = await response.json()
                setCompletedOrders(data.orders)
            } else {
                toast.error('Siparişler yüklenemedi')
            }
        } catch (error) {
            toast.error('Siparişler yüklenirken hata')
        } finally {
            setLoadingCompleted(false)
        }
    }

    // Sipariş durumunu değiştir
    const updateOrderStatus = async (orderId: string, status: number, statusText: string) => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/orders/${orderId}/status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status, statusText })
            })

            if (response.ok) {
                toast.success('Sipariş durumu güncellendi')
                // Siparişleri yeniden yükle
                fetchCompletedOrders(completedSearchQuery, statusFilter)
                setShowStatusModal(false)
                setSelectedCompletedOrder(null)
            } else {
                toast.error('Sipariş durumu güncellenemedi')
            }
        } catch (error) {
            toast.error('Sipariş durumu güncellenirken hata')
        }
    }

    // Enter tuşu ile tarama
    const handleScanKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            scanBarcode()
        }
    }

    // Durum badge'i
    const getStateBadge = (item: OrderItemWithScan) => {
        switch (item.state) {
            case 'ok':
                return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Tamam</Badge>
            case 'over':
                return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Fazla</Badge>
            case 'short':
                return <Badge className="bg-yellow-500"><AlertTriangle className="w-3 h-3 mr-1" />Eksik</Badge>
            default:
                return <Badge variant="secondary">Bekliyor</Badge>
        }
    }

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex items-center space-x-2">
                <Package className="h-6 w-6" />
                <h1 className="text-2xl font-bold">Barkodlu Sipariş Kontrol</h1>
            </div>

            {/* Sipariş Arama */}
            <Card>
                <CardHeader>
                    <CardTitle>Sipariş Ara</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex space-x-2">
                        <Input
                            placeholder="Sipariş Numarası"
                            value={orderNumber}
                            onChange={(e) => setOrderNumber(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && searchOrder()}
                        />
                        <Button onClick={searchOrder} disabled={loading}>
                            <Search className="w-4 h-4 mr-2" />
                            {loading ? 'Arıyor...' : 'Ara'}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowCompletedOrders(!showCompletedOrders)
                                if (!showCompletedOrders) {
                                    fetchCompletedOrders(completedSearchQuery, statusFilter)
                                }
                            }}
                            disabled={loadingCompleted}
                        >
                            <List className="w-4 h-4 mr-2" />
                            {loadingCompleted ? 'Yükleniyor...' : 'Sipariş Durumları'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Tamamlanan Siparişler Listesi */}
            {showCompletedOrders && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <Calendar className="w-5 h-5 mr-2" />
                            Sipariş Durumları
                        </CardTitle>
                        <div className="flex gap-2 mt-4">
                            <Input
                                placeholder="Sipariş numarası veya müşteri ara..."
                                value={completedSearchQuery}
                                onChange={(e) => setCompletedSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && fetchCompletedOrders(completedSearchQuery, statusFilter)}
                            />
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value)
                                    fetchCompletedOrders(completedSearchQuery, e.target.value)
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">Tüm Durumlar</option>
                                <option value="1">Yeni Sipariş</option>
                                <option value="2">Hazırlanıyor</option>
                                <option value="3">Kısmi Tamamlandı</option>
                                <option value="4">Tamamlandı</option>
                                <option value="5">İptal Edildi</option>
                            </select>
                            <Button onClick={() => fetchCompletedOrders(completedSearchQuery, statusFilter)}>
                                <Search className="w-4 h-4 mr-2" />
                                Ara
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {completedOrders.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">Sipariş bulunamadı</p>
                        ) : (
                            <div className="space-y-3">
                                {completedOrders.map((completedOrder) => (
                                    <div
                                        key={completedOrder.id}
                                        className="border rounded-lg p-4 hover:bg-gray-50"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div
                                                className="flex-1 cursor-pointer"
                                                onClick={() => {
                                                    setOrderNumber(completedOrder.orderNumber)
                                                    setShowCompletedOrders(false)
                                                    searchOrderWithNumber(completedOrder.orderNumber)
                                                }}
                                            >
                                                <h3 className="font-semibold">#{completedOrder.orderNumber}</h3>
                                                <p className="text-sm text-gray-600">
                                                    {completedOrder.email || completedOrder.customerCode || 'Müşteri bilgisi yok'}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {completedOrder.summary?.totalItems || 0} ürün • ₺{completedOrder.netTotal}
                                                </p>
                                            </div>
                                            <div className="text-right flex flex-col gap-2">
                                                <Badge variant={completedOrder.status === 4 ? 'default' : 'secondary'}>
                                                    {completedOrder.statusText}
                                                </Badge>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(completedOrder.updatedAt).toLocaleDateString('tr-TR')}
                                                </p>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setSelectedCompletedOrder(completedOrder)
                                                        setShowStatusModal(true)
                                                    }}
                                                >
                                                    Durum Değiştir
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Sipariş Detayları */}
            {order && (
                <>
                    {/* Başlık Bilgileri */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Sipariş: {order.orderNumber}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Müşteri</p>
                                    <p className="font-semibold">{order.deliveryRecipient || 'Bilinmiyor'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Toplam Ürün</p>
                                    <p className="font-semibold">{order.summary.totalItems} çeşit</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Toplam Adet</p>
                                    <p className="font-semibold">{order.summary.totalQuantity} adet</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">İlerleme</p>
                                    <p className="font-semibold">{order.summary.progress.toFixed(1)}%</p>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="mt-4">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${order.summary.progress}%` }}
                                    ></div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Barkod Tarama */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Barkod Tarama</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex space-x-2">
                                <Input
                                    ref={scanInputRef}
                                    placeholder="Barkod okutun veya yazın..."
                                    value={scanInput}
                                    onChange={(e) => setScanInput(e.target.value)}
                                    onKeyPress={handleScanKeyPress}
                                    className="text-lg"
                                />
                                <Button onClick={scanBarcode} disabled={scanning || !scanInput.trim()}>
                                    {scanning ? 'Taranıyor...' : 'Tara'}
                                </Button>
                                <Button variant="outline" onClick={undoLastScan}>
                                    <Undo className="w-4 h-4 mr-2" />
                                    Geri Al
                                </Button>
                            </div>

                            {/* Son Tarama Sonucu */}
                            {lastScanResult && (
                                <Alert className={`mt-4 ${lastScanResult.result === 'success' ? 'border-green-500' :
                                    lastScanResult.result === 'over' ? 'border-yellow-500' : 'border-red-500'
                                    }`}>
                                    <div className="font-semibold">{lastScanResult.message}</div>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    {/* Ürün Listesi */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Ürün Listesi</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {order.orderItems.map((item) => (
                                    <div key={item.id} className="border rounded-lg p-4 flex items-center space-x-4">
                                        {/* Ürün Fotoğrafı */}
                                        <div className="flex-shrink-0">
                                            {item.product?.image1 && item.product?.image1 !== '[image1]' ? (
                                                <img
                                                    src={item.product.image1}
                                                    alt={item.productName}
                                                    className="w-16 h-16 object-cover rounded cursor-pointer"
                                                    onClick={() => setSelectedImage(item.product?.image1 || '')}
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none'
                                                        e.currentTarget.nextElementSibling?.classList.remove('hidden')
                                                    }}
                                                />
                                            ) : null}
                                            <div className={`w-16 h-16 bg-gray-200 rounded flex items-center justify-center ${item.product?.image1 && item.product?.image1 !== '[image1]' ? 'hidden' : ''
                                                }`}>
                                                <Package className="w-8 h-8 text-gray-400" />
                                            </div>
                                        </div>

                                        {/* Ürün Bilgileri */}
                                        <div className="flex-1">
                                            <h3 className="font-semibold">{item.productName}</h3>
                                            <p className="text-sm text-gray-500">SKU: {item.code}</p>
                                            <p className="text-sm text-gray-500">Barkod: {item.barcode || 'Yok'}</p>
                                        </div>

                                        {/* Durum ve Adet */}
                                        <div className="text-right space-y-2">
                                            {getStateBadge(item)}
                                            <p className="text-lg font-bold">
                                                {item.scannedQty} / {item.quantity}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                Kalan: {Math.max(0, item.quantity - item.scannedQty)}
                                            </p>

                                            {/* Manuel Tik Butonu */}
                                            {item.scannedQty < item.quantity && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => manualTick(item)}
                                                    className="w-full"
                                                >
                                                    <Plus className="w-3 h-3 mr-1" />
                                                    Tik
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Tamamlama Butonu */}
                    <Card>
                        <CardContent className="pt-6">
                            <Button
                                onClick={() => {
                                    console.log('Tamamlama butonu tıklandı, progress:', order.summary.progress)
                                    completeOrder()
                                }}
                                size="lg"
                                className="w-full"
                                disabled={order.summary.progress === 0}
                            >
                                Siparişi Tamamla (Progress: {order.summary.progress.toFixed(1)}%)
                            </Button>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Fotoğraf Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="max-w-2xl max-h-2xl">
                        <img
                            src={selectedImage}
                            alt="Ürün fotoğrafı"
                            className="max-w-full max-h-full object-contain"
                        />
                    </div>
                </div>
            )}

            {/* Durum Değiştirme Modal */}
            {showStatusModal && selectedCompletedOrder && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowStatusModal(false)}
                >
                    <div
                        className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold mb-4">
                            Sipariş Durumunu Değiştir
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Sipariş: #{selectedCompletedOrder.orderNumber}
                        </p>
                        <div className="space-y-3">
                            <Button
                                className="w-full"
                                variant={selectedCompletedOrder.status === 1 ? 'default' : 'outline'}
                                onClick={() => updateOrderStatus(selectedCompletedOrder.id, 1, 'Yeni Sipariş')}
                            >
                                Yeni Sipariş
                            </Button>
                            <Button
                                className="w-full"
                                variant={selectedCompletedOrder.status === 2 ? 'default' : 'outline'}
                                onClick={() => updateOrderStatus(selectedCompletedOrder.id, 2, 'Hazırlanıyor')}
                            >
                                Hazırlanıyor
                            </Button>
                            <Button
                                className="w-full"
                                variant={selectedCompletedOrder.status === 3 ? 'default' : 'outline'}
                                onClick={() => updateOrderStatus(selectedCompletedOrder.id, 3, 'Kısmi Tamamlandı')}
                            >
                                Kısmi Tamamlandı
                            </Button>
                            <Button
                                className="w-full"
                                variant={selectedCompletedOrder.status === 4 ? 'default' : 'outline'}
                                onClick={() => updateOrderStatus(selectedCompletedOrder.id, 4, 'Tamamlandı')}
                            >
                                Tamamlandı
                            </Button>
                            <Button
                                className="w-full"
                                variant={selectedCompletedOrder.status === 5 ? 'default' : 'outline'}
                                onClick={() => updateOrderStatus(selectedCompletedOrder.id, 5, 'İptal Edildi')}
                            >
                                İptal Edildi
                            </Button>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setShowStatusModal(false)}
                            >
                                İptal
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
